/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from "react";
import { Button, Divider, Typography, Dialog, DialogTitle, DialogContent, IconButton, Checkbox, FormControlLabel, Tooltip } from "@material-ui/core";
import { Close, Info } from "@material-ui/icons";
import { tokenOptions } from "../tokenOptions";
import { useStylesDark, useStylesLight } from "../../../Styles/VoteCalculatorStyles";
import { castVote, setDelegate, setConvexDaoDelegate, delegationStatus, isDaoDelegatedToConvex, pollForReceipt } from "../../../scripts/scripts";
import {
  votiumDelegateAddress,
  convexTeamDelegateAddress,
  addressRegistryAddress,
  addressRegistryABI,
} from "../../../constants";
import { ethers } from "ethers";

// The Union forwarding address
export const UNION_ADDRESS = "0xcc2a0f5e95c88aabd7b8e0db5c5252820cd47f91";
const UNION_JOIN_URL = "https://llama.airforce/union/member";

function getDisclaimerContent({
  isDelegatedToVotium,
  isForwardedToUnion,
  hasManualVote,
}) {
  if (isDelegatedToVotium && isForwardedToUnion) {
    if (hasManualVote) {
      return {
        title: null,
        body:
          "Since you have already cast a vote in this round, Votium delegation can no longer vote on your behalf. For future rounds, consider allowing Votium to vote for you, and The Union to consolidate your rewards.",
        prominent: false,
        showDelegateButton: false,
      };
    }
    return {
      title: "No manual optimization needed",
      body:
        "You're already set up for automated voting and reward conversion. Votium balances your votes to optimize returns, while The Union converts the various tokens into your rewards of choice. Using the calculator or voting manually is likely to reduce your overall returns.",
      prominent: false,
      showDelegateButton: false,
    };
  }

  return {
    showSetupFlow: true,
    prominent: false,
    showDelegateButton: false,
  };
}

// ─── Pure algorithm ─────────────────────────────────────────────────────────
// Ports NsnapVote.js logic to browser-side JS.
// Returns { gauges: address[], weights: number[], gaugeResults: [...] }
// weights are integers denominated to max_weight (1000000 on Curve/Fx GaugeVoting).
const TARGET_DENOM = 1000000;
function computeOptimalVote(bribesArray, snapshotChoices, userVote, vlCVXBalance, selectedTokenAddrs) {
  // Derive user's current vlCVX votes per gauge so we can subtract them from totals
  const userGaugeVotes = {};
  if (userVote?.gauges?.length && userVote?.weights?.length) {
    const totalWeight = userVote.weights.reduce((s, v) => s + v, 0);
    if (totalWeight > 0) {
      userVote.gauges.forEach((gaugeAddr, i) => {
        const weight = userVote.weights[i] || 0;
        if (gaugeAddr) {
          userGaugeVotes[gaugeAddr.toLowerCase()] =
            vlCVXBalance * (weight / totalWeight);
        }
      });
    }
  }

  const shot = {};    // gauge → { total, usd, adjUsd, hasMax }
  const incents = {}; // gauge → [{ usdTotal, usdMaxPer, perUsd, votes }]
  let adjTotal = 0;
  let votesTotal = vlCVXBalance; // user's votes will be re-allocated

  bribesArray.forEach((bribe) => {
    const gauge = bribe.gauge.toLowerCase();
    const userVotesOnGauge = userGaugeVotes[gauge] || 0;
    const othersVotes = Math.max(0, (bribe.votes || 0) - userVotesOnGauge);

    // Only include tokens the user selected
    const filteredTokens = (bribe.tokens || []).filter(
      (t) => t.tokenAddress && selectedTokenAddrs.has(t.tokenAddress.toLowerCase())
    );
    if (filteredTokens.length === 0) return;

    shot[gauge] = { total: othersVotes };
    incents[gauge] = [];

    filteredTokens.forEach((token) => {
      const usdTotal = token.dollarValue || 0;
      const usdMaxPer =
        token.maxPerVote && typeof token.maxPerVote === "number" && token.maxPerVote > 0
          ? token.maxPerVote
          : 0;
      let perUsd = othersVotes > 0 ? usdTotal / othersVotes : usdTotal;
      if (usdMaxPer > 0 && perUsd > usdMaxPer) perUsd = usdMaxPer;

      incents[gauge].push({ usdTotal, usdMaxPer, perUsd, votes: othersVotes });
    });

    shot[gauge].usd = incents[gauge].reduce((s, p) => s + p.usdTotal, 0);
    shot[gauge].adjUsd = incents[gauge].reduce((s, p) => s + p.perUsd * (p.votes || 1), 0);
    shot[gauge].hasMax = incents[gauge].some((p) => p.usdMaxPer > 0);
    adjTotal += shot[gauge].adjUsd;
    votesTotal += othersVotes;
  });

  // Weed out gauges whose adjusted yield is below the running average
  let weeding = true;
  while (weeding) {
    const baseAvg = votesTotal > 0 ? adjTotal / votesTotal : 0;
    weeding = false;
    for (const gauge of Object.keys(incents)) {
      const gaugeTotal = shot[gauge].total || 1;
      if (shot[gauge].adjUsd / gaugeTotal < baseAvg) {
        votesTotal -= shot[gauge].total;
        adjTotal -= shot[gauge].adjUsd;
        delete incents[gauge];
        delete shot[gauge];
        weeding = true;
      }
    }
  }

  if (Object.keys(incents).length === 0) {
    return { gauges: [], weights: [], gaugeResults: [] };
  }

  const baseAvg = adjTotal / votesTotal;

  // First pass: compute raw vote units per gauge
  const voteObject = {};
  let myVoteTotal = 0;

  for (const gauge of Object.keys(incents)) {
    const gaugeInc = incents[gauge];
    if (gaugeInc.length > 1 && shot[gauge].hasMax) {
      gaugeInc.sort((a, b) => b.perUsd - a.perUsd);
      const given =
        (gaugeInc[0].perUsd * gaugeInc[0].votes) / baseAvg - gaugeInc[0].votes;
      let wokeAvg = baseAvg;
      for (let i = 1; i < gaugeInc.length; i++) {
        let newPer = gaugeInc[i].usdTotal / (given + (gaugeInc[i].votes || 1));
        if (gaugeInc[i].usdMaxPer > 0 && newPer > gaugeInc[i].usdMaxPer)
          newPer = gaugeInc[i].usdMaxPer;
        wokeAvg -= newPer;
      }
      voteObject[gauge] =
        wokeAvg > 0
          ? (gaugeInc[0].perUsd * gaugeInc[0].votes) / wokeAvg - gaugeInc[0].votes
          : 0;
    } else {
      const firstVotes = gaugeInc[0].votes || 1;
      voteObject[gauge] = shot[gauge].usd / baseAvg - firstVotes;
    }
    if (voteObject[gauge] < 0) voteObject[gauge] = 0;
    myVoteTotal += voteObject[gauge];
  }

  // Scale raw units to the user's actual vlCVX balance
  if (myVoteTotal > 0) {
    for (const gauge of Object.keys(voteObject)) {
      if (voteObject[gauge] > 0)
        voteObject[gauge] *= vlCVXBalance / myVoteTotal;
    }
  }

  // Convert to on-chain gauges[] + weights[]
  const gaugeResults = [];

  for (const [gauge, votes] of Object.entries(voteObject)) {
    if (votes <= 0) continue;
    // Prefer checksummed address from snapshotChoices when available
    const choiceMatch = snapshotChoices.find(
      (c) => c.toLowerCase() === gauge
    );
    const gaugeAddr = choiceMatch || gauge;
    const weight = Math.floor((votes / vlCVXBalance) * TARGET_DENOM);
    if (weight <= 0) continue;
    const bribe = bribesArray.find((b) => b.gauge.toLowerCase() === gauge);
    const userVotesOnGauge = vlCVXBalance * (weight / TARGET_DENOM);
    const totalVotesOnGauge = (shot[gauge].total || 0) + userVotesOnGauge;
    const dollarPerVlCVX = totalVotesOnGauge > 0 ? shot[gauge].usd / totalVotesOnGauge : 0;
    gaugeResults.push({
      gauge: gaugeAddr,
      poolName: bribe?.poolName || gaugeAddr,
      weight,
      pct: "0",
      dollarPerVlCVX,
    });
  }

  // Distribute rounding remainder among top gauges
  let denomTotal = gaugeResults.reduce((s, r) => s + r.weight, 0);
  let remaining = TARGET_DENOM - denomTotal;
  let fillIdx = 0;
  while (remaining > 0 && gaugeResults.length > 0) {
    const r = gaugeResults[fillIdx % gaugeResults.length];
    r.weight++;
    remaining--;
    fillIdx++;
  }

  gaugeResults.forEach((r) => {
    r.pct = ((r.weight / TARGET_DENOM) * 100).toFixed(1);
  });
  gaugeResults.sort((a, b) => b.weight - a.weight);

  return {
    gauges: gaugeResults.map((r) => r.gauge),
    weights: gaugeResults.map((r) => r.weight),
    gaugeResults,
  };
}

// ─── Token helpers (mirrors BribesRow) ──────────────────────────────────────
function getTokenImage(tokenAddress) {
  if (!tokenAddress) return null;
  const token = tokenOptions.find(
    (o) => o.value.toLowerCase() === tokenAddress.toLowerCase()
  );
  return token
    ? React.cloneElement(token.img, {
        style: { width: "20px", height: "20px", borderRadius: "50%" },
        title: token.label,
      })
    : null;
}

// ─── Component ───────────────────────────────────────────────────────────────
const VoteCalculator = (props) => {
  const classesLight = useStylesLight();
  const classesDark = useStylesDark();
  const [classes, setClasses] = useState(classesDark);
  const [selectedTokens, setSelectedTokens] = useState(new Set());
  const [open, setOpen] = useState(false);
  const [proceededPastDisclaimer, setProceededPastDisclaimer] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDelegating, setIsDelegating] = useState(false);
  const [delegatePending, setDelegatePending] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // { ok: bool, msg: string } | null
  const [delegateStatus, setDelegateStatus] = useState(null);
  const [hasVotedThisSession, setHasVotedThisSession] = useState(false);
  const [delegateToConvexTeam, setDelegateToConvexTeam] = useState(true);
  // null = not yet fetched; avoid racing Delegate click against the RPC check.
  const [daoDelegatedToConvex, setDaoDelegatedToConvex] = useState(null);
  // Prefer prop when provided; otherwise filled by the on-open registry fetch
  // so Header/MobileHeader (which omit the prop) still mark Union members done.
  const [fetchedForwardingAddress, setFetchedForwardingAddress] = useState(null);
  const forwardingAddress =
    props.forwardingAddress !== undefined
      ? props.forwardingAddress
      : fetchedForwardingAddress;
  // Only submit Convex DAO delegation when recommended (checkbox on) AND we
  // know the user isn't already delegated for the next round.
  const willDelegateConvex =
    delegateToConvexTeam && daoDelegatedToConvex === false;

  useEffect(() => {
    if (props.ctheme === "light") setClasses(classesLight);
    else setClasses(classesDark);
  }, [props.ctheme]);

  // Check existing Convex DAO delegation when the dialog opens so we only
  // recommend it to users who haven't already delegated for the next round.
  useEffect(() => {
    if (!open || !props.provider || !props.address) return;
    let cancelled = false;
    setDaoDelegatedToConvex(null);
    isDaoDelegatedToConvex(props.address, props.provider).then((delegated) => {
      if (!cancelled) setDaoDelegatedToConvex(delegated);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, props.address]);

  // Resolve Union forwarding when the dialog opens if the parent didn't pass it
  // (Header/MobileHeader). Parents that already know it skip the extra RPC.
  useEffect(() => {
    if (!open || props.forwardingAddress !== undefined) return;
    if (!props.provider || !props.address) {
      setFetchedForwardingAddress(null);
      return;
    }
    let cancelled = false;
    const fetchForwarding = async () => {
      try {
        const contract = new ethers.Contract(
          addressRegistryAddress,
          addressRegistryABI,
          props.provider
        );
        const res = await contract.registry(props.address);
        if (cancelled) return;
        if (
          res.start.toNumber() !== 0 &&
          res.expiration.toNumber() > Math.floor(Date.now() / 1000)
        ) {
          setFetchedForwardingAddress(res.to);
        } else {
          setFetchedForwardingAddress(null);
        }
      } catch (e) {
        console.log("Couldn't fetch forwarding address", e);
        if (!cancelled) setFetchedForwardingAddress(null);
      }
    };
    fetchForwarding();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, props.address, props.forwardingAddress]);

  // Build deduplicated token list across all gauges
  const uniqueTokens = useMemo(() => {
    const seen = new Set();
    const result = [];
    (props.bribesArray || []).forEach((bribe) => {
      (bribe.tokens || []).forEach((token) => {
        const addr = token.tokenAddress?.toLowerCase();
        if (addr && !seen.has(addr)) {
          seen.add(addr);
          result.push({ tokenAddress: token.tokenAddress, symbol: token.symbol });
        }
      });
    });
    return result;
  }, [props.bribesArray]);

  // Reset selection each time the dialog opens
  const handleOpen = () => {
    setSelectedTokens(new Set());
    setSubmitStatus(null);
    setDelegateStatus(null);
    setDelegateToConvexTeam(true);
    setDaoDelegatedToConvex(null);
    setProceededPastDisclaimer(false);
    setOpen(true);
    props.onOpenChange?.(true);
  };

  const handleClose = () => {
    setOpen(false);
    setProceededPastDisclaimer(false);
    props.onOpenChange?.(false);
  };

  const handleProceedPastDisclaimer = () => {
    // setupOnly has no in-dialog calculator — never pretend this opens a vote.
    if (props.setupOnly) {
      handleClose();
      return;
    }
    setProceededPastDisclaimer(true);
  };

  const handleDelegate = async () => {
    if (!props.provider || !props.address) {
      props.connect?.();
      return;
    }
    setIsDelegating(true);
    setDelegateStatus(null);
    let hashes;
    try {
      // Submit without waiting for mining so the next wallet prompt can appear
      // right after the first signature (MetaMask still shows one dialog at a time).
      const votiumTx = await setDelegate(props.provider, votiumDelegateAddress, {
        wait: false,
      });
      hashes = [votiumTx.hash];
      if (willDelegateConvex) {
        const convexTx = await setConvexDaoDelegate(
          props.provider,
          convexTeamDelegateAddress,
          { wait: false }
        );
        hashes.push(convexTx.hash);
      }
    } catch (e) {
      setDelegateStatus({ ok: false, msg: e.message || "Delegation failed." });
      setIsDelegating(false);
      return;
    }

    // Signatures captured — unblock the UI so the user can move on to step 2
    // while we poll for on-chain confirmation in the background.
    setIsDelegating(false);
    setDelegatePending(true);
    setDelegateStatus({
      ok: true,
      msg: "Delegation submitted — confirming on-chain…",
    });
    const provider = props.provider;
    try {
      await Promise.all(hashes.map((h) => pollForReceipt(provider, h)));
      setDelegateStatus({
        ok: true,
        msg: willDelegateConvex
          ? "Successfully delegated to Votium and Convex!"
          : "Successfully delegated to Votium!",
      });
      if (props.refreshDelegation) props.refreshDelegation();
    } catch (e) {
      setDelegateStatus({
        ok: false,
        msg: e.message || "Delegation failed to confirm.",
      });
    } finally {
      setDelegatePending(false);
    }
  };

  // Effective voting power: only use userVote.vp when the vote belongs to the
  // connected address — if it came from the delegate, that vp is the delegate's,
  // not the user's, so fall back to the on-chain vlCVX balance only.
  const ownVoteVP = !props.voteIsFromDelegate ? (props.userVote?.vp || 0) : 0;
  const effectiveVP = Math.max(props.vlCVXBalance || 0, ownVoteVP);
  const vpIncludesDelegated =
    !props.voteIsFromDelegate &&
    props.userVote?.vp &&
    props.userVote.vp > (props.vlCVXBalance || 0);

  const { gaugeResults, gauges, weights } = useMemo(() => {
    if (
      !props.bribesArray?.length ||
      !props.snapshotChoices?.length ||
      !props.proposal ||
      !effectiveVP
    ) {
      return { gaugeResults: [], gauges: [], weights: [] };
    }
    return computeOptimalVote(
      props.bribesArray,
      props.snapshotChoices,
      props.userVote,
      effectiveVP,
      selectedTokens
    );
  }, [
    selectedTokens,
    props.bribesArray,
    props.snapshotChoices,
    props.userVote,
    effectiveVP,
    props.proposal,
  ]);

  useEffect(() => {
    setSubmitStatus(null);
  }, [
    selectedTokens,
    props.bribesArray,
    props.snapshotChoices,
    props.userVote,
    effectiveVP,
    props.proposal,
  ]);

  const toggleToken = (addr) => {
    setSelectedTokens((prev) => {
      const next = new Set(prev);
      const key = addr.toLowerCase();
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleCastVote = async () => {
    if (!props.provider || !props.address || !props.proposal) return;
    setIsSubmitting(true);
    setSubmitStatus(null);
    const result = await castVote(
      props.provider,
      props.proposal.platform,
      props.address,
      gauges,
      weights
    );
    setIsSubmitting(false);
    if (result.success) {
      setSubmitStatus({ ok: true, msg: "Vote cast successfully!" });
      setHasVotedThisSession(true);
    } else {
      setSubmitStatus({ ok: false, msg: result.error || "Vote failed." });
    }
  };

  const { isVotiumFuture, isVotiumFull, isVotiumPendingOnly } =
    delegationStatus(props.delegateAddress);
  // Step 1 (delegation) counts as complete once Votium is the delegate going
  // forward; full optimization additionally requires the current round.
  const isDelegatedToVotium = isVotiumFuture;
  const isForwardedToUnion =
    forwardingAddress &&
    forwardingAddress.toLowerCase() === UNION_ADDRESS.toLowerCase();
  const hasManualVote =
    hasVotedThisSession ||
    (props.userVote && !props.voteIsFromDelegate);
  // Discourage manual voting only when Votium is the delegate for BOTH the
  // active round and going forward, AND rewards are forwarded to the Union.
  // A lapsing delegate (current only) must still reach the setup flow so they
  // can re-delegate for the next round.
  const disclaimer = getDisclaimerContent({
    isDelegatedToVotium: isVotiumFull,
    isForwardedToUnion,
    hasManualVote,
  });
  const daoDelegationReady = daoDelegatedToConvex !== null;
  const canDelegate =
    props.provider &&
    props.address &&
    !isVotiumFuture &&
    !isDelegating &&
    daoDelegationReady;
  const delegateButtonDisabled =
    isDelegating ||
    delegatePending ||
    (!!props.provider && !!props.address && !canDelegate);
  const showProminentEntry = !isVotiumFull && !isForwardedToUnion;

  const canSubmit =
    props.provider &&
    props.address &&
    props.proposal &&
    gaugeResults.length > 0 &&
    !isSubmitting;

  if (!props.setupOnly && !props.bribesArray?.length) return null;

  const useCustomTrigger = Boolean(props.triggerLabel);
  const triggerLabel =
    props.triggerLabel ||
    (showProminentEntry ? "Optimize Your Rewards" : "Manual Vote Calculator");

  return (
    <div
      className={
        useCustomTrigger
          ? classes.rootInline
          : showProminentEntry
            ? classes.rootProminent
            : classes.root
      }
    >
      {!props.hideTrigger && (
        <Button
          className={
            useCustomTrigger
              ? undefined
              : showProminentEntry
                ? classes.openButtonProminent
                : classes.openButton
          }
          classes={props.triggerClasses}
          onClick={handleOpen}
          endIcon={props.triggerEndIcon}
          disabled={props.triggerDisabled}
          disableRipple={props.triggerDisableRipple}
        >
          {triggerLabel}
        </Button>
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        classes={{ paper: classes.dialogPaper }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Typography component="span" className={classes.dialogTitleText}>
            {showProminentEntry ? "Optimize Your Rewards" : "Manual Vote Calculator"}
          </Typography>
          <IconButton
            size="small"
            className={classes.closeButton}
            onClick={handleClose}
            aria-label="Close"
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent className={classes.dialogContent}>
          {!proceededPastDisclaimer ? (
            disclaimer.showSetupFlow ? (
              <div className={classes.setupFlow}>
                <div className={classes.setupStep}>
                  <div
                    className={
                      isDelegatedToVotium
                        ? `${classes.setupStepNumber} ${classes.setupStepNumberComplete}`
                        : classes.setupStepNumber
                    }
                  >
                    {isDelegatedToVotium ? "✓" : "1"}
                  </div>
                  <div className={classes.setupStepContent}>
                    <Typography className={classes.setupStepTitle}>
                      Delegate to Votium to optimize rewards
                    </Typography>
                    <Typography className={classes.setupStepBody}>
                      Votium automatically balances and casts your votes each round to
                      maximize returns. Delegation takes effect starting next round.
                    </Typography>
                    {isDelegatedToVotium ? (
                      <Typography className={classes.setupStepComplete}>
                        {isVotiumPendingOnly
                          ? "Delegated to Votium (starts next round)"
                          : "Delegated to Votium"}
                      </Typography>
                    ) : (
                      <>
                        {daoDelegatedToConvex === false && (
                        <FormControlLabel
                          className={classes.convexDelegateCheckbox}
                          control={
                            <Checkbox
                              checked={delegateToConvexTeam}
                              onChange={(e) =>
                                setDelegateToConvexTeam(e.target.checked)
                              }
                              color="primary"
                              size="small"
                              disabled={isDelegating}
                            />
                          }
                          label={
                            <span className={classes.convexDelegateLabel}>
                              Also delegate DAO votes to the Convex team
                              <Tooltip
                                title="Most CVX holders have their interests aligned with the Convex DAO, but are not active voters. We recommend delegating to Convex so those interests stay represented in the Convex DAO governance."
                                placement="top"
                                arrow
                              >
                                <Info
                                  className={classes.convexDelegateInfoIcon}
                                  onClick={(e) => e.preventDefault()}
                                />
                              </Tooltip>
                            </span>
                          }
                        />
                        )}
                        <Button
                          className={`${classes.delegateButton} ${classes.setupStepButton}`}
                          onClick={handleDelegate}
                          disabled={delegateButtonDisabled}
                        >
                          {isDelegating
                            ? willDelegateConvex
                              ? "Waiting for signatures…"
                              : "Waiting for signature…"
                            : delegatePending
                              ? "Confirming…"
                              : !daoDelegationReady && props.provider && props.address
                                ? "Checking delegation…"
                              : props.provider && props.address
                                ? "Delegate to Votium"
                                : "Connect wallet to delegate"}
                        </Button>
                        {delegateStatus && (
                          <Typography
                            className={`${classes.statusText} ${
                              delegateStatus.ok ? classes.statusSuccess : classes.statusError
                            }`}
                          >
                            {delegateStatus.msg}
                          </Typography>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div className={classes.setupStep}>
                  <div
                    className={
                      isForwardedToUnion
                        ? `${classes.setupStepNumber} ${classes.setupStepNumberComplete}`
                        : classes.setupStepNumber
                    }
                  >
                    {isForwardedToUnion ? "✓" : "2"}
                  </div>
                  <div className={classes.setupStepContent}>
                    <Typography className={classes.setupStepTitle}>
                      Join The Union to simplify your rewards
                    </Typography>
                    <Typography className={classes.setupStepBody}>
                      The Union converts the various incentive tokens from each round into
                      your selected reward, completing a fully automated experience.
                    </Typography>
                    {isForwardedToUnion ? (
                      <Typography className={classes.setupStepComplete}>
                        Forwarded to The Union
                      </Typography>
                    ) : (
                      <Button
                        className={`${classes.unionButton} ${classes.setupStepButton}`}
                        onClick={() =>
                          window.open(UNION_JOIN_URL, "_blank", "noopener,noreferrer")
                        }
                      >
                        Join The Union
                      </Button>
                    )}
                  </div>
                </div>

                {!props.noActiveVote && (
                <div className={classes.setupStep}>
                  <div className={classes.setupStepNumber}>3</div>
                  <div className={classes.setupStepContent}>
                    <Typography className={classes.setupStepTitle}>
                      Only this round — vote manually
                    </Typography>
                    <Typography className={classes.setupStepBody}>
                      {props.setupOnly
                        ? "Delegation and Union forwarding apply from the next round onward. Close this dialog and use the Manual Vote Calculator on the auction page to cast this round's vote."
                        : "Delegation and Union forwarding apply from the next round onward. Use the calculator to optimize and cast your vote for this round only."}
                    </Typography>
                    <Button
                      className={`${classes.proceedButton} ${classes.setupStepButton}`}
                      onClick={handleProceedPastDisclaimer}
                    >
                      {props.setupOnly ? "Got it" : "Vote manually this round"}
                    </Button>
                  </div>
                </div>
                )}
                {props.noActiveVote && (
                <div className={classes.setupStep}>
                  <div className={classes.setupStepNumber}>3</div>
                  <div className={classes.setupStepContent}>
                    <Typography className={classes.setupStepTitle}>
                      Ready for the next round
                    </Typography>
                    <Typography className={classes.setupStepBody}>
                      There is no active vote right now. Once the next gauge proposal opens,
                      Votium will cast optimized votes on your behalf if you have delegated.
                    </Typography>
                    <Button
                      className={`${classes.proceedButton} ${classes.setupStepButton}`}
                      onClick={handleClose}
                    >
                      Got it
                    </Button>
                  </div>
                </div>
                )}
              </div>
            ) : (
            <>
              <div
                className={
                  disclaimer.prominent
                    ? classes.disclaimerProminent
                    : classes.disclaimerBlock
                }
              >
                {disclaimer.title && (
                  <Typography className={classes.disclaimerTitle}>
                    {disclaimer.title}
                  </Typography>
                )}
                <Typography
                  className={
                    disclaimer.prominent
                      ? classes.disclaimerTextProminent
                      : classes.disclaimerText
                  }
                >
                  {disclaimer.body}
                </Typography>
              </div>

              {disclaimer.showDelegateButton && (
                <div className={classes.disclaimerActions}>
                  <Button
                    className={
                      disclaimer.prominent
                        ? classes.delegateButtonProminent
                        : classes.delegateButton
                    }
                    onClick={handleDelegate}
                    disabled={delegateButtonDisabled}
                  >
                    {isDelegating
                      ? "Waiting for signature…"
                      : props.provider && props.address
                        ? "Delegate to Votium"
                        : "Connect wallet to delegate"}
                  </Button>
                  {delegateStatus && (
                    <Typography
                      className={`${classes.statusText} ${
                        delegateStatus.ok ? classes.statusSuccess : classes.statusError
                      }`}
                    >
                      {delegateStatus.msg}
                    </Typography>
                  )}
                </div>
              )}

              <div className={classes.disclaimerActions}>
                <Button
                  className={classes.proceedButton}
                  onClick={handleProceedPastDisclaimer}
                >
                  I understand, proceed with manual voting
                </Button>
              </div>
            </>
            )
          ) : (
            <>
            {vpIncludesDelegated && (
          <div className={classes.warningBox}>
            Using your last recorded voting power of{" "}
            <strong>{effectiveVP.toLocaleString("en-US", { maximumFractionDigits: 0 })} vlCVX</strong>{" "}
            (includes vlCVX delegated to you by others). If your delegated power has
            changed since your last vote, the allocation will update after you cast.
          </div>
        )}

        {uniqueTokens.length === 0 ? (
          <Typography className={classes.noTokensText}>
            No incentive tokens found for this round.
          </Typography>
        ) : (
          <>
            <Typography className={classes.sectionLabel}>
              Tokens to optimise for
            </Typography>
            <div className={classes.tokenGrid}>
              {uniqueTokens.map((token) => {
                const addr = token.tokenAddress.toLowerCase();
                const isSelected = selectedTokens.has(addr);
                return (
                  <button
                    key={addr}
                    className={`${classes.tokenChip} ${isSelected ? classes.tokenChipSelected : ""}`}
                    onClick={() => toggleToken(token.tokenAddress)}
                  >
                    {getTokenImage(token.tokenAddress)}
                    <span>{token.symbol || addr.slice(0, 6)}</span>
                  </button>
                );
              })}
            </div>

            <Divider className={classes.divider} />

            {gaugeResults.some((row) => {
              const bribe = props.bribesArray.find((b) => b.gauge.toLowerCase() === row.gauge);
              return bribe?.tokens?.some((t) => t.tokenAddress && !selectedTokens.has(t.tokenAddress.toLowerCase()));
            }) && (
              <div className={classes.warningBox} style={{ marginBottom: "0.75em", marginTop: 0 }}>
                One or more pools in the allocation have additional reward tokens that are not selected. Deselecting tokens excludes them from the yield calculation — reselect them to include their value.
              </div>
            )}

            <Typography className={classes.sectionLabel}>
              Suggested allocation
            </Typography>

            {gaugeResults.length === 0 ? (
              <Typography className={classes.emptyText}>
                No eligible gauges for the selected tokens.
              </Typography>
            ) : (
              <table className={classes.allocationTable}>
                <thead>
                  <tr>
                    <th
                      className={classes.tableHead}
                      style={{ textAlign: "left", paddingBottom: "0.5em" }}
                    >
                      Pool
                    </th>
                    <th
                      className={classes.tableHead}
                      style={{ textAlign: "right", paddingBottom: "0.5em" }}
                    >
                      Allocation
                    </th>
                    <th
                      className={classes.tableHead}
                      style={{ textAlign: "right", paddingBottom: "0.5em" }}
                    >
                      $/vlCVX
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {gaugeResults.map((row) => (
                    <tr key={row.gauge} className={classes.tableRow}>
                      <td className={classes.tableCell}>{row.poolName}</td>
                      <td
                        className={classes.tableCell}
                        style={{ textAlign: "right" }}
                      >
                        <span
                          className={classes.allocationBar}
                          style={{ width: `${Math.min(60, parseFloat(row.pct))}px` }}
                        />
                        <span className={classes.pctText}>{row.pct}%</span>
                      </td>
                      <td
                        className={classes.tableCell}
                        style={{ textAlign: "right" }}
                      >
                        ${row.dollarPerVlCVX.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {gaugeResults.length > 0 && (
              <div className={classes.submitActions}>
                <Button
                  className={classes.submitButton}
                  onClick={handleCastVote}
                  disabled={!canSubmit}
                >
                  {isSubmitting ? "Waiting for transaction…" : "Cast on-chain vote"}
                </Button>

                {submitStatus && (
                  <Typography
                    className={`${classes.statusText} ${
                      submitStatus.ok ? classes.statusSuccess : classes.statusError
                    }`}
                  >
                    {submitStatus.msg}
                  </Typography>
                )}
              </div>
            )}
          </>
        )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VoteCalculator;

export const RewardsOptimizationPrompt = (props) => {
  const classesLight = useStylesLight();
  const classesDark = useStylesDark();
  const [classes, setClasses] = useState(classesDark);

  useEffect(() => {
    if (props.ctheme === "light") setClasses(classesLight);
    else setClasses(classesDark);
  }, [props.ctheme]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const { isVotiumFull } = delegationStatus(props.delegateAddress);
  const isForwardedToUnion =
    props.forwardingAddress &&
    props.forwardingAddress.toLowerCase() === UNION_ADDRESS;

  // Keep the dialog mounted through refreshDelegation so a mid-flow success
  // (isVotiumFull flipping true) cannot tear it down before the user finishes.
  if (isVotiumFull && isForwardedToUnion && !dialogOpen) {
    return null;
  }

  return (
    <VoteCalculator
      setupOnly
      noActiveVote
      hideTrigger={isVotiumFull && isForwardedToUnion}
      onOpenChange={setDialogOpen}
      ctheme={props.ctheme}
      delegateAddress={props.delegateAddress}
      forwardingAddress={props.forwardingAddress}
      provider={props.provider}
      address={props.address}
      refreshDelegation={props.refreshDelegation}
      connect={props.connect}
      bribesArray={[]}
    />
  );
};
